import { Box, Typography, Link, useTheme, useMediaQuery, IconButton, Collapse } from '@mui/material';
import { useState, useEffect } from 'react';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getMenus, Menu as MenuType } from '../../api/shop';
import { MenuItem as ShopMenuItem } from '../../types/shop';
import { useConfig } from '../../api/config';

export default function Footer() {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [menus, setMenus] = useState<MenuType[]>([]);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const { config } = useConfig();

  // Helper function to get the correct label based on current language
  const getMenuItemLabel = (item: ShopMenuItem): string => {
    const currentLang = i18n.language;
    if (currentLang === 'zh' && item.labelZh) {
      return item.labelZh;
    } else if (currentLang === 'cn' && item.labelCn) {
      return item.labelCn;
    }
    return item.label;
  };

  // Helper function to get the correct menu name based on current language
  const getMenuName = (menu: MenuType): string => {
    const currentLang = i18n.language;
    if (currentLang === 'zh' && menu.nameZh) {
      return menu.nameZh;
    } else if (currentLang === 'cn' && menu.nameCn) {
      return menu.nameCn;
    }
    return menu.name;
  };

  const toggleSection = (sectionTitle: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle]
    }));
  };

  const fetchMenus = async () => {
    try {
      const data = await getMenus();
      setMenus(data);

      // Initialize expanded sections for footer menus
      const footerMenus = data.filter(menu => menu.position.startsWith('Menu-footer-') && menu.isActive);
      const initialExpandedSections: { [key: string]: boolean } = {};
      footerMenus.forEach(menu => {
        initialExpandedSections[menu.name] = false;
      });
      setExpandedSections(initialExpandedSections);
    } catch (error) {
      console.error('Error fetching menus:', error);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, []);

  // Helper function to extract relative path from full URL
  const extractRelativePath = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      // If it's already a relative path, return as is
      return url.startsWith('/') ? url : `/${url}`;
    }
  };

  // Get footer menu items from API and sort them by position (Menu-footer-1, Menu-footer-2, etc.)
  const footerMenus = menus
    .filter(menu => menu.position.startsWith('Menu-footer-') && menu.isActive)
    .sort((a, b) => {
      const aNum = parseInt(a.position.replace('Menu-footer-', ''));
      const bNum = parseInt(b.position.replace('Menu-footer-', ''));
      return aNum - bNum;
    });

  const footerLinks = footerMenus.length > 0 ? footerMenus.map(menu => ({
    title: getMenuName(menu),
    links: menu.items.map(item => ({ label: getMenuItemLabel(item), link: extractRelativePath(item.link) })),
  })) : [
    {
      title: 'My Account',
      links: [
        { label: 'Track Orders', link: '#' },
        { label: 'Wish List', link: '#' },
        { label: 'Reviews', link: '#' },
        { label: 'Contact Us', link: '#' },
        { label: 'HATSUHIKEN MEMBERS Benefit', link: '#' },
        { label: 'Affiliate Program', link: '#' },
        { label: 'Student & Grad Discount', link: '#' },
        { label: 'Healthcare & Teacher Discount', link: '#' },
      ],
    },
    {
      title: 'Help',
      links: [
        { label: 'Customer Service', link: '#' },
        { label: 'FAQs', link: '#' },
        { label: 'Shipping', link: '#' },
        { label: 'Billing', link: '#' },
        { label: 'Return & Exchanges', link: '#' },
        { label: 'Hotlines', link: '#' },
      ],
    },
    {
      title: 'About Hatsuhiken',
      links: [
        { label: 'HATSUHIKEN Platform', link: '#' },
        { label: 'About Us', link: '#' },
        { label: 'Business', link: '#' },
      ],
    },
    {
      title: 'Terms & Policies',
      links: [
        { label: 'Terms & Conditions', link: '#' },
        { label: 'Legal Information', link: '#' },
        { label: 'Privacy Policy', link: '#' },
        { label: 'Email Collection Policy', link: '#' },
        { label: 'Influencer Terms & Conditions', link: '#' },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#F2EFEA',
        bgcolor: '#F2EFEA',
        borderTop: 1,
        mt: isMobile ? 1 : 4,
        py: isMobile ? 1 : 4,
        borderColor: 'grey.200',
        justifySelf: 'center',
      }}
    >
      <Box maxWidth="lg" sx={{ justifySelf: 'center', px: 4 }}>
        {/* Top Links */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 0,
          }}
        >
          {footerLinks.map(section => (
            <Box
              key={section.title}
              sx={{
                flex: { xs: '1 1 100%', md: '1 1 0' },
                minWidth: { xs: 'auto', md: 200 },
                maxWidth: { xs: '100%', md: 300 },
                mb: { xs: 1, md: 0 },
                textAlign: { xs: 'left', md: 'left' },
              }}
            >
              {/* Mobile: Collapsible Section Header */}
              {isMobile ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    py: 1,
                    px: 2,
                  }}
                  onClick={() => toggleSection(section.title)}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    {section.title}
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ p: 0, color: '#666' }}
                  >
                    {expandedSections[section.title] ? (
                      <KeyboardArrowUp />
                    ) : (
                      <KeyboardArrowDown />
                    )}
                  </IconButton>
                </Box>
              ) : (
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {section.title}
                </Typography>
              )}

              {/* Links - Collapsible on Mobile */}
              {isMobile ? (
                <Collapse in={expandedSections[section.title]}>
                  <Box sx={{ pt: 1 }}>
                    {section.links.map((link, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{ color: 'text.secondary', mb: 2, pl: 2 }}
                      >
                        <Link href={link.link} underline="none" color="inherit">
                          {link.label}
                        </Link>
                      </Typography>
                    ))}
                  </Box>
                </Collapse>
              ) : (
                section.links.map((link, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ color: 'text.secondary', mb: 0.5 }}
                  >
                    <Link href={link.link} underline="none" color="inherit">
                      {link.label}
                    </Link>
                  </Typography>
                ))
              )}
            </Box>
          ))}
        </Box>
        {/* Divider */}
        <Box
          sx={{ borderBottom: `2px solid ${theme.palette.custom.brown}`, my: 4, borderRadius: '1px' }}
        />
        {/* Bottom Info Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: { xs: 'center', lg: 'stretch' },
            gap: { xs: 3, lg: 4 },
            textAlign: { xs: 'center', lg: 'left' },
          }}
        >
          {/* Left: Corporation Name */}
          <Box
            sx={{
              flex: { xs: 'none', lg: 0.5 },
              minWidth: { xs: 'auto', lg: 200 },
              display: 'flex',
              alignItems: 'center',
              pr: { xs: 0, lg: 2 },
              order: { xs: 1, lg: 1 },
            }}
          >
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{ color: '#3D3C3C', fontSize: '16px' }}
            >
              {config?.businessName || t('footer.companyName')}
            </Typography>
          </Box>
          {/* Middle: Company Details */}
          <Box
            sx={{
              flex: { xs: 'none', lg: 2 },
              minWidth: { xs: 'auto', lg: 320 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              py: { xs: 0, lg: 1 },
              order: { xs: 2, lg: 2 },
              width: { xs: '100%', lg: 'auto' },
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              display="block"
              sx={{ fontSize: '12px' }}
            >
              {config?.storeName || t('footer.brandName')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              display="block"
              sx={{ fontSize: '12px' }}
            >
              {config?.storeAddressLine1 && config?.storeCity ?
                `${config.storeAddressLine1}${config.storeAddressLine2 ? ', ' + config.storeAddressLine2 : ''}, ${config.storeCity}, ${config.storeCountryState || ''} ${config.storePostcode || ''}`.trim()
                : t('footer.address')}
            </Typography>
          </Box>
          {/* Right: Social + Payment/Delivery */}
          <Box
            sx={{
              flex: { xs: 'none', lg: 2.75 },
              minWidth: { xs: 'auto', lg: 320 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', lg: 'flex-start' },
              gap: 1,
              order: { xs: 3, lg: 3 },
              width: { xs: '100%', lg: 'auto' },
            }}
          >
            {/* Social Icons */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', lg: 'flex-start' },
              gap: 2,
              mb: 1,
              width: '100%'
            }}>
              <a
                href="http://www.instagram.com/hatsuhiken/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: 'pointer' }}
              >
                <img
                  src="/icons/ic_svg.svg"
                  alt="Instagram"
                  width={32}
                  height={32}
                />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61579932770052"
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: 'pointer' }}
              >
                <img
                  src="/icons/ic_facebook.svg"
                  alt="Facebook"
                  width={32}
                  height={32}
                />
              </a>
            </Box>
            {/* Payment/Delivery Methods Box */}
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: 3,
                p: { xs: 1, md: 2 },
                boxShadow: 0,
                minWidth: { xs: 'auto', lg: 340 },
                width: '100%',
                maxWidth: { xs: '100%', lg: 'none' },
              }}
            >
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 2,
                gap: { xs: 1, sm: 0 }
              }}>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{
                    mr: { xs: 1, sm: 2 },
                    fontSize: '14px',
                    mb: { xs: 1, sm: 0 },
                  }}
                >
                  {t('footer.paymentMethod')}
                </Typography>
                <img
                  src="/icons/ic_payment_method.svg"
                  alt="Payment Methods"
                  height={24}
                  style={{ background: 'white', borderRadius: 4 }}
                />
              </Box>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                mt: 2,
                gap: { xs: 1, sm: 0 }
              }}>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{
                    mr: { xs: 0, sm: 2 },
                    fontSize: '14px',
                    mb: { xs: 1, sm: 0 }
                  }}
                >
                  {t('footer.deliveryMethod')}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                }}>
                  <img
                    src="/icons/sf_express.svg"
                    alt="SF Express"
                    style={{
                      width: 96,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
